var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import { MessageEnum } from '../models/message.enum';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { AddLabel, RemoveLabel, SetLabel, SetLabelsShown, SetAllLabelsShown } from 'src/app/story/state/labels/story.labels.actions';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
function getDistinct(values) {
    var valuesSet = new Set(values);
    return Array.from(valuesSet.values());
}
function getLabels(store) {
    return store.selectSnapshot(StoryLabelsState.getChapterLabels);
}
function getLabelById(store, labelId) {
    var labels = getLabels(store);
    return findLabelById(labels, labelId);
}
function getMarkerById(human, labelId) {
    return human.scene.markers.markers[labelId];
}
function findLabelById(labels, labelId) {
    return labels.find(function (label) { return labelId === label.id; });
}
function findLabelsByIds(labels, labelIds) {
    return labels.filter(function (label) { return labelIds.includes(label.id); });
}
function findLabelsByObjectId(labels) {
    var objectIds = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        objectIds[_i - 1] = arguments[_i];
    }
    var allFoundLabels = objectIds.map(function (objectId) {
        var labelsByObjectId = labels.filter(function (label) { return objectId === label.objectId; });
        return labelsByObjectId;
    });
    // flatten list
    var flatLabels = [].concat.apply([], __spread(allFoundLabels));
    return getDistinct(flatLabels);
}
function convertAnnotationToLabel(human, store, labelsService, request) {
    var chapter = store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    // if the request objectId isn't in the scene, replace it with undefined.
    var objectIdSanitized = human.scene.objects[request.objectId]
        ? request.objectId
        : undefined;
    // API annotation request -> app annotation
    var annotation = {
        annotationId: request.annotationId,
        title: request.title,
        description: request.description,
        objectId: objectIdSanitized,
        pos: request.position,
        occludable: request.occludable,
        labelOffset: request.labelOffset,
        shown: request.shown,
        labelShown: request.shown
    };
    // Prevent labels from being unoccludable.
    // The engine requires markers to have a defined position if they are to be occludable.
    // If the request has an object Id, and no position, assign a position.
    // https://github.com/biodigital-human/human-engine/blob/master/src/scene/markers.js#L121
    // https://github.com/biodigital-human/human-engine/blob/master/src/scene/markers.js#L121
    if (annotation.objectId && annotation.pos === undefined) {
        annotation.pos = human.scene.objects[annotation.objectId].center;
    }
    // app annotation -> app label
    return labelsService.deserializeAnnotation(annotation, chapter, LabelThemeEnum.Annotation);
}
function convertLabelToAnnotation(human, label) {
    // associated marker
    var marker = getMarkerById(human, label.id);
    // app label -> API annotation response
    return {
        annotationId: label.id,
        objectId: label.objectId || null,
        title: label.title,
        description: label.description,
        shown: label.shown,
        occludable: label.occludable,
        // live position tracked by marker
        position: marker
            ? {
                x: marker.pos[0],
                y: marker.pos[1],
                z: marker.pos[2]
            }
            : null,
        canvasPosition: marker
            ? {
                x: marker.canvasPos[0],
                y: marker.canvasPos[1]
            }
            : null
    };
}
function convertLabelsToAnnotationsInfo(human, labels) {
    // annotation id -> annotation info
    return labels.reduce(function (info, label) {
        info[label.id] = convertLabelToAnnotation(human, label);
        return info;
    }, {});
}
function createLabelFromAnnotation(store, annotation) {
    var label = getLabelById(store, annotation.annotationId);
    if (label) {
        return __assign({}, label, annotation);
    }
    else {
        return null;
    }
}
function createLabel(store, label) {
    // validate required fields for label creation
    if (label.objectId || label.pos) {
        store.dispatch(new AddLabel(label));
        return label;
    }
    else {
        return null;
    }
}
function updateLabel(store, label) {
    // patch label w/ annotation data
    if (label) {
        store.dispatch(new SetLabel(label));
    }
    return true;
}
function deleteLabel(store, label) {
    if (label) {
        store.dispatch(new RemoveLabel(label));
    }
    return true;
}
function showAllLabels(store, shown) {
    store.dispatch(new SetAllLabelsShown(shown));
    return true;
}
function createIdMapFromValue(ids, value) {
    return ids.reduce(function (acc, id) {
        var _a;
        return (__assign({}, acc, (_a = {}, _a["" + id] = value, _a)));
    }, {});
}
function filterMap(idMap, compareFn) {
    return Object.entries(idMap)
        .filter(function (_a) {
        var _b = __read(_a, 2), key = _b[0], value = _b[1];
        return compareFn(key, value);
    })
        .reduce(function (acc, _a) {
        var _b;
        var _c = __read(_a, 2), key = _c[0], value = _c[1];
        return (__assign({}, acc, (_b = {}, _b["" + key] = value, _b)));
    }, {});
}
function filterMapByValue(idMap, compareValue) {
    return filterMap(idMap, function (key, value) { return value === compareValue; });
}
function filterIdMapKeysByValue(idMap, compareValue) {
    return Object.keys(filterMapByValue(idMap, compareValue));
}
function filterLabelsByMapValue(labels, idMap, compareValue) {
    var labelIds = filterIdMapKeysByValue(idMap, compareValue);
    return findLabelsByIds(labels, labelIds);
}
function createLabelMapFromObjectIdMap(labels, objectIdMap, compareValue) {
    var allIds = filterIdMapKeysByValue(objectIdMap, compareValue) // get list of object ids
        .map(function (objectId) { return findLabelsByObjectId(labels, objectId); }) // get labels by object
        .map(function (objectLabels) { return objectLabels.map(function (a) { return a.id; }); }); // get ids
    var flatIds = [].concat.apply([], __spread(allIds));
    return createIdMapFromValue(flatIds, compareValue);
}
function getLabelsShownMap(store, params) {
    var annotations = params.annotations, objects = params.objects, replace = params.replace, type = params.type;
    var annotationsIdMap = annotations || {};
    var objectsIdsMap = objects || {};
    var labels = getLabels(store);
    var idShownMap = __assign({}, filterMapByValue(annotationsIdMap, true), filterMapByValue(annotationsIdMap, false), createLabelMapFromObjectIdMap(labels, objectsIdsMap, true), createLabelMapFromObjectIdMap(labels, objectsIdsMap, false)
    // #TODO[AC]: filter by type???
    );
    // replace: hide all others
    if (replace) {
        return __assign({}, createIdMapFromValue(labels.map(function (label) { return label.id; }), false), idShownMap);
    }
    else {
        return idShownMap;
    }
}
function setLabelsShown(store, labelShownMap) {
    var labels = getLabels(store);
    var labelsToHide = filterLabelsByMapValue(labels, labelShownMap, false);
    var labelsToShow = filterLabelsByMapValue(labels, labelShownMap, true);
    // show labels
    store.dispatch(new SetLabelsShown(labelsToShow, true));
    // hide labels
    store.dispatch(new SetLabelsShown(labelsToHide, false));
}
export function getAnnotationEvents(human, store) {
    return [
        {
            eventName: EventsEnum.AnnotationsCreated,
            aliasEvents: [EventsEnum.LabelCreated],
            mapFn: function (label) { return convertLabelToAnnotation(human, label); }
        },
        {
            eventName: EventsEnum.AnnotationsDestroyed,
            aliasEvents: [EventsEnum.LabelDestroyed],
            mapFn: function (label) { return ({ annotationId: label.id }); }
        },
        {
            eventName: EventsEnum.AnnotationsShown,
            aliasEvents: [EventsEnum.LabelsShown]
        },
        {
            eventName: EventsEnum.AnnotationsHidden,
            aliasEvents: [EventsEnum.LabelsHidden]
        }
    ];
}
export function getAnnotationProcedures(human, store, labelsService) {
    return [
        {
            procName: MessageEnum.AnnotationsInfo,
            actionFn: function () { return getLabels(store); },
            mapFnOut: function (labels) { return convertLabelsToAnnotationsInfo(human, labels); }
        },
        {
            procName: MessageEnum.AnnotationsCreate,
            mapFnIn: function (request) { return convertAnnotationToLabel(human, store, labelsService, request); },
            actionFn: function (label) { return createLabel(store, label); },
            mapFnOut: function (label) { return label ? convertLabelToAnnotation(human, label) : null; }
        },
        {
            procName: MessageEnum.AnnotationsUpdate,
            mapFnIn: function (params) { return createLabelFromAnnotation(store, params); },
            actionFn: function (label) { return updateLabel(store, label); }
        },
        {
            procName: MessageEnum.AnnotationsSetTitle,
            mapFnIn: function (params) { return createLabelFromAnnotation(store, params); },
            actionFn: function (label) { return updateLabel(store, label); }
        },
        {
            procName: MessageEnum.AnnotationsSetDescription,
            mapFnIn: function (params) { return createLabelFromAnnotation(store, params); },
            actionFn: function (label) { return updateLabel(store, label); }
        },
        {
            procName: MessageEnum.AnnotationsDestroy,
            mapFnIn: function (annotationId) { return getLabelById(store, annotationId); },
            actionFn: function (label) { return deleteLabel(store, label); }
        },
        {
            procName: MessageEnum.ShowAnnotations,
            aliases: [MessageEnum.SetAnnotationsShown],
            actionFn: function () { return showAllLabels(store, true); }
        },
        {
            procName: MessageEnum.HideAnnotations,
            actionFn: function () { return showAllLabels(store, false); }
        },
        {
            procName: MessageEnum.AnnotationsSetLabelsShown,
            mapFnIn: function (params) { return getLabelsShownMap(store, params); },
            actionFn: function (labelShownMap) { return setLabelsShown(store, labelShownMap); }
        }
    ];
}
